import { useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { subscriptionMeOptions } from '../api/subscription.queries';
import {
    CHECKOUT_SETTLE_POLL_BACKOFF_AFTER_MS,
    CHECKOUT_SETTLE_POLL_INTERVAL_MS,
    CHECKOUT_SETTLE_SLOW_POLL_INTERVAL_MS,
    SESSION_GENERATE_ACTION,
} from '../config/subscription.config';
import { isCheckoutSettling } from '../lib/is-checkout-settling';

export function useCheckoutReturn(checkoutSessionId: string | undefined, onSettled: () => void) {
    const pollStartedAtRef = useRef<number | null>(null);

    const { data: subscription, status } = useQuery({
        ...subscriptionMeOptions(),
        // The Stripe redirect reloads the app, so the subscription/updated push that would
        // otherwise reveal the activation can land while the socket is still reconnecting.
        refetchInterval: (query) => {
            if (!isCheckoutSettling(query.state.data, checkoutSessionId)) {
                pollStartedAtRef.current = null;
                return false;
            }

            pollStartedAtRef.current ??= Date.now();
            const elapsed = Date.now() - pollStartedAtRef.current;

            return elapsed < CHECKOUT_SETTLE_POLL_BACKOFF_AFTER_MS
                ? CHECKOUT_SETTLE_POLL_INTERVAL_MS
                : CHECKOUT_SETTLE_SLOW_POLL_INTERVAL_MS;
        },
    });

    const isWaitingCheckout = status === 'success' && !!checkoutSessionId;
    const isCheckoutSettled =
        isWaitingCheckout && !isCheckoutSettling(subscription, checkoutSessionId);

    const isResubscribeReturn = subscription?.status === 'CANCELED' && !subscription.isTrial;

    const wasResubscribeReturnRef = useRef(false);
    const settledHandledRef = useRef(false);

    useEffect(() => {
        if (isWaitingCheckout && !isCheckoutSettled && isResubscribeReturn) {
            wasResubscribeReturnRef.current = true;
        }
    }, [isWaitingCheckout, isCheckoutSettled, isResubscribeReturn]);

    useEffect(() => {
        if (!isCheckoutSettled || settledHandledRef.current) return;
        settledHandledRef.current = true;
        if (wasResubscribeReturnRef.current) {
            const includedSessions = subscription?.limits[SESSION_GENERATE_ACTION]?.limit;
            toast.success(
                includedSessions != null
                    ? `${includedSessions} session plan activated`
                    : 'Your plan is now active',
            );
        }
        onSettled();
    }, [isCheckoutSettled, onSettled, subscription]);

    return {
        isWaitingCheckout,
        isCheckoutSettled,
        isResubscribeSettling: isWaitingCheckout && !isCheckoutSettled && isResubscribeReturn,
    };
}
